import { type Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import type { ApiResponse, PaginatedListResponse } from '@shared/http/types';

import { createHttpParams } from '@shared/http/utils';

import type { RoleDto, UserDto, UserCountersDto } from './dto';
import type { Role, User, UsersQuery, UserCounters, CreateUserPayload, UpdateUserPayload } from './models';
import {
  mapRoleFromDto,
  mapUserFromDto,
  mapUsersQueryToDto,
  mapUserCountersFromDto,
  mapCreateUserPayloadToDto,
  mapUpdateUserPayloadToDto,
} from './mappers';

import { buildApiEndpoint } from '@core/http';
import { mapList, mapDataNonNull, mapPaginatedList } from '@shared/http/operators';

@Injectable({
  providedIn: 'root',
})
export class UserRepository {
  private readonly http = inject(HttpClient);

  getUsers(query?: UsersQuery): Observable<PaginatedListResponse<User>> {
    return this.http
      .get<PaginatedListResponse<UserDto>>(buildApiEndpoint('users.list'), {
        params: query ? createHttpParams(mapUsersQueryToDto(query)) : undefined,
      })
      .pipe(mapPaginatedList(mapUserFromDto));
  }

  getRoles(): Observable<Role[]> {
    return this.http.get<PaginatedListResponse<RoleDto>>(buildApiEndpoint('roles.list')).pipe(mapList(mapRoleFromDto));
  }

  createUser(payload: CreateUserPayload): Observable<User> {
    const dto = mapCreateUserPayloadToDto(payload);
    return this.http
      .post<ApiResponse<UserDto>>(buildApiEndpoint('users.list'), dto)
      .pipe(mapDataNonNull(mapUserFromDto));
  }

  updateUser(id: number, payload: UpdateUserPayload): Observable<User> {
    const dto = mapUpdateUserPayloadToDto(payload);
    return this.http
      .put<ApiResponse<UserDto>>(buildApiEndpoint('users.detail', { userId: id }), dto)
      .pipe(mapDataNonNull(mapUserFromDto));
  }

  deleteUser(id: number): Observable<void> {
    return this.http.delete<void>(buildApiEndpoint('users.detail', { userId: id }));
  }

  resendInvite(id: number): Observable<void> {
    return this.http.post<void>(buildApiEndpoint('users.resend', { userId: id }), null);
  }

  getUser(id: number): Observable<User> {
    return this.http
      .get<ApiResponse<UserDto>>(buildApiEndpoint('users.detail', { userId: id }))
      .pipe(mapDataNonNull(mapUserFromDto));
  }

  getUserCounters(id: number): Observable<UserCounters> {
    return this.http
      .get<ApiResponse<UserCountersDto>>(buildApiEndpoint('users.counters', { userId: id }))
      .pipe(mapDataNonNull(mapUserCountersFromDto));
  }
}
